import type { ReactNode } from 'react'
import { cn } from '../ui/utils'

import { BLOG_LOCALES, type BlogLocale } from './locale'

const DEFAULT_LABELS: Record<BlogLocale, string> = {
  ru: 'RU',
  en: 'EN',
}

export interface BlogLanguageSwitcherProps {
  currentLocale: BlogLocale
  ariaLabel: string
  labels?: Partial<Record<BlogLocale, string>>
  hrefs?: Partial<Record<BlogLocale, string>>
  onSelectLocale?: (locale: BlogLocale) => void
  disableMissingHrefs?: boolean
  renderLink?: (
    locale: BlogLocale,
    href: string,
    className: string,
    children: ReactNode,
    onClick: () => void,
  ) => ReactNode
  className?: string
}

export function BlogLanguageSwitcher({
  currentLocale,
  ariaLabel,
  labels,
  hrefs,
  onSelectLocale,
  disableMissingHrefs,
  renderLink,
  className,
}: BlogLanguageSwitcherProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('flex items-center gap-0.5 rounded-md border bg-card p-0.5', className)}
    >
      {BLOG_LOCALES.map((locale) => {
        const selected = locale === currentLocale
        const href = hrefs?.[locale]
        const label = labels?.[locale] ?? DEFAULT_LABELS[locale]
        const disabled = !selected && (!onSelectLocale || (disableMissingHrefs && !href))
        const controlClassName = cn(
          'rounded px-2 py-0.5 text-xs font-medium transition-colors',
          selected
            ? 'bg-accent text-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
          disabled && 'pointer-events-none opacity-50',
        )

        if (href) {
          const onClick = () => onSelectLocale?.(locale)
          return renderLink ? (
            <span key={locale}>
              {renderLink(locale, href, controlClassName, label, onClick)}
            </span>
          ) : (
            <a
              key={locale}
              href={href}
              onClick={onClick}
              aria-current={selected ? 'page' : undefined}
              className={controlClassName}
            >
              {label}
            </a>
          )
        }

        return (
          <button
            key={locale}
            type="button"
            onClick={() => onSelectLocale?.(locale)}
            aria-pressed={selected}
            disabled={disabled}
            className={controlClassName}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
